/**
 * Account-Page — under the user-dropdown (bottom-left of the sidebar).
 *
 * Houses everything that's about the logged-in user's identity:
 *   - Display name + email
 *   - Password change (via reauthenticate + updatePassword)
 *   - Future: 2FA, sessions, delete-account
 *
 * Kept deliberately small for now — we'll grow this when the user asks
 * for more knobs.
 */
import * as React from 'react';
import {
  AtSign,
  KeyRound,
  Loader2,
  Save,
  ShieldCheck,
  User as UserIcon,
} from 'lucide-react';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  updatePassword,
} from 'firebase/auth';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { useToast } from '../components/ui/toast';
import { useUser } from '../auth/AuthProvider';
import { auth } from '../lib/firebase';

export function AccountPage() {
  return (
    <div className="space-y-6">
      <ProfileSummaryCard />
      <PasswordCard />
      <PasswordResetMailCard />
    </div>
  );
}

function ProfileSummaryCard() {
  const { user, profile } = useUser();
  const fullName =
    [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim() ||
    profile?.displayName ||
    user?.email?.split('@')[0] ||
    '';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <UserIcon className="h-5 w-5 text-primary" />
          <CardTitle>Meine Daten</CardTitle>
        </div>
        <CardDescription>
          Diese Informationen sind nur für dich sichtbar. Deinen Namen oder dein
          Profilbild änderst du jederzeit über dein Profilmenü unten links.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Anbieter-Feld auf Wunsch entfernt (Brief Section 7) — der
            Provider-Identifier verwirrt Endkunden mehr als er nützt. */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Dein Name" value={fullName || '—'} />
          <Field label="Deine E-Mail-Adresse" value={user?.email ?? '—'} />
          <Field
            label="Deine Konto-Nummer"
            value={user?.uid ?? '—'}
            mono
          />
        </div>
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={mono ? 'mt-1 font-mono text-xs' : 'mt-1 text-sm font-medium'}>
        {value}
      </div>
    </div>
  );
}

function PasswordCard() {
  const { user } = useUser();
  const { toast } = useToast();
  const [current, setCurrent] = React.useState('');
  const [next, setNext] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  async function onChange() {
    if (!user || !user.email) {
      toast({ title: 'Nicht eingeloggt', variant: 'destructive' });
      return;
    }
    if (next.length < 8) {
      toast({
        title: 'Passwort zu kurz',
        description: 'Mindestens 8 Zeichen.',
        variant: 'destructive',
      });
      return;
    }
    if (next !== confirm) {
      toast({ title: 'Passwörter stimmen nicht überein', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      // Firebase requires recent re-auth to change password.
      const cred = EmailAuthProvider.credential(user.email, current);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, next);
      toast({ title: 'Passwort geändert' });
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      const code = (err as { code?: string }).code ?? '';
      const msg =
        code === 'auth/wrong-password' || code === 'auth/invalid-credential'
          ? 'Aktuelles Passwort ist falsch.'
          : code === 'auth/weak-password'
            ? 'Neues Passwort ist zu schwach.'
            : (err as Error).message;
      toast({ title: 'Fehler', description: msg, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" />
          <CardTitle>Neues Passwort wählen</CardTitle>
        </div>
        <CardDescription>
          Aus Sicherheitsgründen brauchen wir dein bisheriges Passwort, bevor du
          ein neues festlegst.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="cur">Bisheriges Passwort</Label>
          <Input
            id="cur"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
          />
        </div>
        <Separator />
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="new">Dein neues Passwort</Label>
            <Input
              id="new"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              minLength={8}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm">Bitte noch einmal eingeben</Label>
            <Input
              id="confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              minLength={8}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={() => void onChange()} disabled={busy}>
            {busy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Neues Passwort speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PasswordResetMailCard() {
  const { user } = useUser();
  const { toast } = useToast();
  const [busy, setBusy] = React.useState(false);

  async function onSend() {
    if (!user?.email) {
      toast({ title: 'Keine E-Mail-Adresse hinterlegt', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, user.email);
      toast({
        title: 'Mail verschickt',
        description: `Reset-Link wurde an ${user.email} versandt.`,
      });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <AtSign className="h-5 w-5 text-primary" />
          <CardTitle>Passwort nicht mehr griffbereit?</CardTitle>
        </div>
        <CardDescription>
          Kein Problem. Wir senden dir einen sicheren Reset-Link an deine
          hinterlegte E-Mail-Adresse, mit dem du in wenigen Sekunden wieder
          Zugriff hast.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button variant="outline" onClick={() => void onSend()} disabled={busy}>
          {busy ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ShieldCheck className="mr-2 h-4 w-4" />
          )}
          Reset-Link an meine E-Mail senden
        </Button>
      </CardContent>
    </Card>
  );
}
